import { FC, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as DriveIcon } from "../../../assets/driveIcon.svg";
import { ReactComponent as PlusCircleIcon } from "../../../assets/plus-circle.icon.svg";
import { ReactComponent as LinkIcon } from "../../../assets/link.icon.svg";
import { ReactComponent as TivaIcon } from "../../../assets/tivaIcon.svg";
import { ProviderType } from "../../../common/enums/provider-type.enum";
import { ProvidersContainer } from "../../../common/containers/providers/providers.container";
import LinkGoogleDriveView from "../link-google-drive/link-google-drive";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import { LoaderView } from "../../../components/loader/loader.comp";

interface DashboardItem {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    link?: string;
    action?: () => void;
    className?: string;
}

export const DashboardButton = ({
    icon,
    title,
    link,
    onClick,
    className
}: {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    link?: string;
    onClick?: () => void;
    className?: string;
}) => {
    return link ? (
        <Link className={`dashboard-button ${className ?? ""}`} to={link} onClick={onClick}>
            <SvgIcon className="dashboard-button__icon" component={icon} inheritViewBox />
            <span className="dashboard-button__text">{title}</span>
        </Link>
    ) : (
        <a className={`dashboard-button ${className ?? ""}`} onClick={onClick}>
            <SvgIcon className="dashboard-button__icon" component={icon} inheritViewBox />
            <span className="dashboard-button__text">{title}</span>
        </a>
    );
};

const DashboardView = () => {
    const providerPathname = "knowledge-base";
    const { providers, loadProviders } = ProvidersContainer.useContainer();
    const [items, setItems] = useState<DashboardItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [addSourceModalOpen, setAddSourceModalOpen] = useState<boolean>(false);

    const handleAddSourceModalOpen = () => {
        setAddSourceModalOpen(true);
    };

    const handleAddSourceModalClose = () => {
        setAddSourceModalOpen(false);
    };

    const handleAddSourceSuccess = () => {
        loadProviders();
    };

    useEffect(() => {
        if (!providers.length) return;

        setItems([
            ...providers
                .sort((a, b) => a.id - b.id)
                .map(provider => ({
                    icon: DriveIcon,
                    title:
                        provider.name === ProviderType.fileUploader
                            ? "My Files"
                            : provider.displayName,
                    link: `/${providerPathname}/${provider.id}`,
                    className:
                        provider.name === ProviderType.fileUploader
                            ? "dashboard-button--purple"
                            : provider.name === ProviderType.docebo
                              ? undefined
                              : "dashboard-button--pink"
                })),
            {
                icon: PlusCircleIcon,
                title: "Add Source",
                action: handleAddSourceModalOpen,
                className: "dashboard-button--blue"
            },
            {
                icon: TivaIcon,
                title: "TIVA Assistant",
                link: `${window.location.protocol}//${window.location.host.replace("admin.", "")}`,
                className: "dashboard-button--tiva"
            },
            {
                icon: LinkIcon,
                title: "Invite Links",
                link: "/manage-invites",
                className: "dashboard-button--green"
            }
        ]);
        setIsLoading(false);
    }, [providers]);

    return (
        <HeaderLayout>
            <div className="dashboard-view">
                <div className="dashboard-body">
                    <div className="dashboard-content">
                        {items.map(item => (
                            <DashboardButton
                                key={crypto.randomUUID()}
                                icon={item.icon}
                                title={item.title}
                                link={item.link}
                                onClick={item.action}
                                className={item.className}
                            />
                        ))}
                    </div>
                </div>
                <LoaderView show={isLoading} />
            </div>
            <ModalTemplate
                open={addSourceModalOpen}
                width={654}
                handleClose={handleAddSourceModalClose}
            >
                <LinkGoogleDriveView
                    handleClose={handleAddSourceModalClose}
                    handleSuccess={handleAddSourceSuccess}
                />
            </ModalTemplate>
        </HeaderLayout>
    );
};

export default DashboardView;
